/*
 * The More group's folders (LEA-311, LEA-314; docs/2026-10-26-bounces-decision.md, section 8;
 * docs/2026-10-27-more-folders-build-plan.md, design 5; docs/2026-10-19-inbox-sub-inboxes-plan.md,
 * "Where they sit in the UI"). Mail nobody must answer: read-only rows in the noise weight, no Done,
 * Snooze, label, draft or bulk action, no count, "Open in Bison" on every row. One page for every
 * folder; Bounces alone adds its Hard and Soft tabs, a kind on each row and its quiet 7-day line.
 * Its own page, not an inbox place, so nothing the inbox counts, selects or acts on reaches it. A
 * message's text is read from Email Bison as it opens, never stored.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { appCount, appDateTime } from "../../src/app-dates.ts";
import {
  BOUNCES_PAGE_SIZE,
  type BounceRow,
  type MORE_FOLDER_PAGE_SIZE,
  type MoreRow,
  type PlainMoreFolder,
} from "../../src/message-index/contract.ts";
import { LIST, resizeList } from "../../src/pane-layout.ts";
import { BackIcon, ExternalLinkIcon } from "../icons.tsx";
import { Pager } from "../pager.tsx";
import { PaneDivider } from "../pane-divider.tsx";
import type { Shell } from "../shell/app-shell.tsx";
import { useTabTitle } from "../shell/tab-title.ts";
import type { FolderChoice, FolderPart, MoreFolder } from "../shell-nav.ts";
import { folderHref } from "../shell-nav.ts";
import { Tip } from "../tip.tsx";
import { inboxApi } from "./api.ts";
import { rowTime } from "./list.tsx";
import { FoldedPlaces, foldedPanelOf } from "./list-column.tsx";

const KIND_NAMES: Record<BounceRow["kind"], string | null> = {
  hard: "Hard",
  soft: "Soft",
  // Unplaced bounces stay under Bounces itself; a row says nothing it cannot say for sure.
  unplaced: null,
};
const PART_NAMES: Record<FolderPart, string> = { hard: "Hard bounces", soft: "Soft bounces" };
/** Every folder pages at the same size; this stops compiling if the two contracts part ways. */
const SIZE: typeof MORE_FOLDER_PAGE_SIZE = BOUNCES_PAGE_SIZE;

/** How a folder speaks of itself: its name, one of its messages, many of them. */
type FolderWords = {
  readonly name: string;
  /** One message, lower case, with its article: "a bounce". */
  readonly one: string;
  /** Many, lower case: "bounces". */
  readonly many: string;
  /** What the opened text is: a bounce is a notice, the rest are messages. */
  readonly text: "notice" | "message";
  /** The one line under the title, on the two folders whose name alone does not say what is in them. */
  readonly line: string | null;
  /** What the reading pane says before anything is open. */
  readonly choose: string;
};

const PLAIN_CHOOSE =
  "Its text is read from Email Bison when you open it. Mail here is answered in Bison, not from LeadCue.";
const WORDS: Record<MoreFolder, FolderWords> = {
  "auto-replies": {
    name: "Auto-replies",
    one: "an auto-reply",
    many: "auto-replies",
    text: "message",
    line: null,
    choose: PLAIN_CHOOSE,
  },
  bounces: {
    name: "Bounces",
    one: "a bounce",
    many: "bounces",
    text: "notice",
    line: null,
    choose:
      "Its text is read from Email Bison when you open it. Bounces are not answered from LeadCue, and stopping a bounced lead is Bison's.",
  },
  "other-campaigns": {
    name: "Other campaigns",
    one: "a reply",
    many: "replies on other campaigns",
    text: "message",
    line: "Replies on Bison campaigns this workspace has not turned on in LeadCue.",
    choose: PLAIN_CHOOSE,
  },
  spam: {
    name: "Spam",
    one: "a message",
    many: "spam messages",
    text: "message",
    line: null,
    choose: PLAIN_CHOOSE,
  },
  untracked: {
    name: "Untracked (in Bison)",
    one: "a message",
    many: "untracked messages",
    text: "message",
    line: "Mail Bison kept for this workspace but could not tie to a campaign. Mail Bison filters out, such as most warmup traffic, is not here.",
    choose: PLAIN_CHOOSE,
  },
};
const noun = (words: FolderWords) => words.one.replace(/^an? /, "");
const capital = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** A listed row: a bounce carries its kind, every other folder's row none. */
type FolderRow = MoreRow & { readonly kind?: BounceRow["kind"] };
type FolderList = {
  readonly rows: readonly FolderRow[];
  readonly total: number;
  readonly complete: boolean;
  readonly capped: boolean;
  /** Bounces only: how many arrived in the last 7 days. */
  readonly lastSevenDays?: number;
};
type FolderMessage = FolderRow &
  ({ state: "ready"; text: string } | { state: "source_unavailable" });

const plain = (folder: MoreFolder): PlainMoreFolder | null =>
  folder === "bounces" ? null : folder;

function listOf(
  workspaceId: string,
  folder: FolderChoice,
  page: number,
  signal: AbortSignal,
): Promise<FolderList> {
  const other = plain(folder.folder);
  return other
    ? inboxApi.moreFolder(workspaceId, other, page, signal)
    : inboxApi.bounces(workspaceId, { kind: folder.part, page }, signal);
}

function messageOf(
  workspaceId: string,
  folder: MoreFolder,
  ref: string,
  signal: AbortSignal,
): Promise<FolderMessage> {
  const other = plain(folder);
  return other
    ? inboxApi.moreMessage(workspaceId, other, ref, signal)
    : inboxApi.bounce(workspaceId, ref, signal);
}

/** Where in the folder the address is: the part, the page, and the message open, if any. */
function useFolderPlace(folder: FolderChoice) {
  const search = useRouterState({ select: (state) => state.location.searchStr });
  const params = new URLSearchParams(search);
  const page = Math.max(1, Number.parseInt(params.get("page") ?? "1", 10) || 1);
  const ref = params.get("ref");
  return { page, ref: ref && /^[1-9]\d{0,15}$/.test(ref) ? ref : null, part: folder.part };
}

function placeHref(
  workspaceId: string,
  folder: FolderChoice,
  place: { page: number; ref: string | null },
): string {
  const href = folderHref(workspaceId, folder);
  const extra = new URLSearchParams();
  if (place.page > 1) extra.set("page", String(place.page));
  if (place.ref) extra.set("ref", place.ref);
  const tail = extra.toString();
  return tail ? `${href}&${tail}` : href;
}

/** "Open in Bison" as a quiet icon link: Bison owns this mail, and attaching it to a lead. */
function OpenInBison(props: { url: string | null; subject: string; label?: boolean }) {
  if (!props.url) return null;
  if (props.label)
    return (
      <a
        className="lcr-linklike lcr-folder-bison"
        href={props.url}
        target="_blank"
        rel="noreferrer"
      >
        Open in Bison <ExternalLinkIcon size={16} />
      </a>
    );
  return (
    <span className="lcr-folder-bison-wrap">
      <a
        className="lcr-icon-button lcr-folder-bison-icon"
        href={props.url}
        target="_blank"
        rel="noreferrer"
        aria-label={`Open in Bison: ${props.subject}`}
      >
        <ExternalLinkIcon size={16} />
      </a>
      <Tip>Open in Bison</Tip>
    </span>
  );
}

function FolderListRow(props: {
  row: FolderRow;
  bounces: boolean;
  open: boolean;
  onOpen: () => void;
}) {
  const { row } = props;
  const kind = row.kind ? KIND_NAMES[row.kind] : null;
  return (
    <div className="lcr-row-wrap lcr-folder-row-wrap" data-plain={!props.bounces || undefined}>
      <button
        type="button"
        className="lcr-row lcr-folder-row"
        aria-current={props.open}
        onClick={props.onOpen}
      >
        <span className="lcr-row-line">
          <span className="lcr-row-subject">{row.subject || "No subject"}</span>
          <time
            className="lcr-row-time"
            dateTime={row.receivedAt}
            title={appDateTime(row.receivedAt)}
          >
            {rowTime(row.receivedAt)}
          </time>
        </span>
        <span className="lcr-row-lead">{row.sender}</span>
        {/* Bounces keep it when empty, so every row is one height and Open in Bison sits on it. */}
        {props.bounces && (
          <span className="lcr-row-chips lcr-bounce-chips">
            {kind && <span className="lcr-bounce-kind">{kind}</span>}
          </span>
        )}
      </button>
      <OpenInBison url={row.bisonUrl} subject={row.subject} />
    </div>
  );
}

/** Bounces' parts, as tabs at every rail width: the rail draws one row per folder. */
function PartTabs(props: {
  workspaceId: string;
  folder: FolderChoice;
  onGo: (href: string) => void;
}) {
  const parts: { part: FolderPart | null; label: string }[] = [
    { part: null, label: "All" },
    { part: "hard", label: "Hard" },
    { part: "soft", label: "Soft" },
  ];
  return (
    <fieldset className="lcr-list-tabs lcr-bounce-tabs">
      <legend className="lcr-visually-hidden">Bounces</legend>
      {parts.map(({ part, label }) => (
        <button
          key={label}
          type="button"
          className="lcr-tab"
          aria-pressed={props.folder.part === part}
          onClick={() => props.onGo(folderHref(props.workspaceId, { ...props.folder, part }))}
        >
          {label}
        </button>
      ))}
    </fieldset>
  );
}

/** What the folder still lacks: the first read under way, or a folder Bison returns only in part. */
function ReadingNote(props: { list: FolderList; words: FolderWords }) {
  const { list, words } = props;
  if (list.capped)
    return (
      <p className="lcr-list-coverage lcr-folder-reading" role="note">
        Email Bison returns at most 15,000 messages of a folder at once, so the oldest {words.many}{" "}
        of the last 90 days are only in Bison.
      </p>
    );
  if (list.complete) return null;
  return (
    <p className="lcr-list-coverage lcr-folder-reading" role="note">
      Still reading the last 90 days from Email Bison. Older {words.many} appear as the read goes
      on.
    </p>
  );
}

/** An empty list never claims "none" while the first read is still running. */
function emptyWords(list: FolderList, words: FolderWords, part: FolderPart | null): string {
  if (!list.complete && !list.capped) return `No ${words.many} read yet.`;
  return part ? `No ${part} bounces in the last 90 days.` : `No ${words.many} in the last 90 days.`;
}

/** The line under the title: Bounces' 7-day number, or what a folder whose name is not enough holds. */
function HeadNote(props: { list: FolderList | undefined; words: FolderWords }) {
  const { list, words } = props;
  if (list?.lastSevenDays !== undefined)
    return (
      <p className="lcr-folder-note">
        {appCount(list.lastSevenDays)} in the last 7 days · read only, answered in Bison
      </p>
    );
  return words.line ? <p className="lcr-folder-note">{words.line}</p> : null;
}

function ListColumn(props: {
  shell: Shell;
  folder: FolderChoice;
  words: FolderWords;
  place: ReturnType<typeof useFolderPlace>;
}) {
  const { shell, folder, words, place } = props;
  const workspaceId = shell.current.id;
  const bounces = folder.folder === "bounces";
  const list = useQuery({
    queryKey: ["inbox-v2", workspaceId, folder.folder, place.part, place.page],
    queryFn: ({ signal }) => listOf(workspaceId, folder, place.page, signal),
    placeholderData: keepPreviousData,
  });
  const data = list.data;
  const name = place.part ? PART_NAMES[place.part] : words.name;
  const go = (next: { page: number; ref: string | null }) =>
    shell.leave(placeHref(workspaceId, folder, next));
  const folded = foldedPanelOf(shell);
  return (
    <aside className="lcr-list lcr-folder-list" aria-label={name}>
      {folded && <FoldedPlaces folded={folded} />}
      <div className="lcr-folder-head">
        <h2 className="lcr-folder-title">{name}</h2>
        <HeadNote list={data} words={words} />
      </div>
      {bounces && <PartTabs workspaceId={workspaceId} folder={folder} onGo={shell.leave} />}
      {data && <ReadingNote list={data} words={words} />}
      <div
        className="lcr-list-body"
        aria-busy={list.isPlaceholderData || undefined}
        data-stale={list.isPlaceholderData || undefined}
      >
        {list.isError && !data && (
          <p className="lcr-opening-failed" role="alert">
            Could not load the {words.many}.{" "}
            <button type="button" className="lcr-linklike" onClick={() => void list.refetch()}>
              Try again
            </button>
          </p>
        )}
        {data?.rows.map((row) => (
          <FolderListRow
            key={row.ref}
            row={row}
            bounces={bounces}
            open={place.ref === row.ref}
            onOpen={() => go({ page: place.page, ref: row.ref })}
          />
        ))}
        {data && data.rows.length === 0 && (
          <p className="lcr-list-empty lcr-folder-empty">{emptyWords(data, words, place.part)}</p>
        )}
        {data && (
          <Pager
            offset={(place.page - 1) * SIZE}
            shown={data.rows.length}
            total={data.total}
            pageSize={SIZE}
            onPage={(offset) => go({ page: offset / SIZE + 1, ref: null })}
            noun={words.name}
          />
        )}
      </div>
    </aside>
  );
}

function OpenMessage(props: { message: FolderMessage; words: FolderWords }) {
  const { message, words } = props;
  const kind = message.kind ? KIND_NAMES[message.kind] : null;
  return (
    <div className="lcr-folder-body">
      <p className="lcr-head-meta lcr-folder-meta">
        <span>{message.sender}</span>
        <span aria-hidden="true"> · </span>
        <time dateTime={message.receivedAt}>{appDateTime(message.receivedAt)}</time>
        {kind && (
          <>
            <span aria-hidden="true"> · </span>
            <span>{kind} bounce</span>
          </>
        )}
      </p>
      {message.state === "ready" ? (
        <div className="lcr-message lcr-received lcr-folder-message">
          <p className="lcr-folder-text">{message.text || `This ${words.text} has no text.`}</p>
        </div>
      ) : (
        <p className="lcr-opening-failed" role="alert">
          Email Bison could not be read just now, so the {words.text}'s text is not shown. It is
          still in Bison.
        </p>
      )}
    </div>
  );
}

function ReadingColumn(props: {
  shell: Shell;
  folder: FolderChoice;
  words: FolderWords;
  place: ReturnType<typeof useFolderPlace>;
}) {
  const { shell, folder, words, place } = props;
  const workspaceId = shell.current.id;
  const ref = place.ref;
  const label = capital(noun(words));
  const opened = useQuery({
    queryKey: ["inbox-v2", workspaceId, folder.folder, "message", ref],
    queryFn: ({ signal }) => messageOf(workspaceId, folder.folder, ref ?? "", signal),
    enabled: ref !== null,
  });
  useTabTitle(opened.data ? opened.data.subject || label : null);
  if (ref === null)
    return (
      <section className="lcr-detail lcr-i2-reading lcr-folder-reading-pane" aria-label={label}>
        <div className="lcr-folder-nothing">
          <h2 className="lcr-folder-nothing-title">Choose {words.one}</h2>
          <p className="lcr-folder-nothing-line">{words.choose}</p>
        </div>
      </section>
    );
  const message = opened.data;
  return (
    <section
      className="lcr-detail lcr-i2-reading lcr-folder-reading-pane"
      aria-label={label}
      aria-busy={opened.isPending || undefined}
    >
      <header className="lcr-head lcr-i2-head lcr-folder-open-head">
        {shell.onPhone && (
          <button
            type="button"
            className="lcr-back lcr-small-button"
            onClick={() =>
              shell.leave(placeHref(workspaceId, folder, { page: place.page, ref: null }))
            }
          >
            <BackIcon size={16} /> {words.name}
          </button>
        )}
        {message ? (
          <h2>{message.subject || "No subject"}</h2>
        ) : (
          <span className="lcr-skeleton lcr-skeleton-title" aria-hidden="true" />
        )}
        {message && <OpenInBison url={message.bisonUrl} subject={message.subject} label />}
      </header>
      {message && <OpenMessage message={message} words={words} />}
      {opened.isError && (
        <p className="lcr-opening-failed" role="alert">
          Could not open this {noun(words)}.{" "}
          <button type="button" className="lcr-linklike" onClick={() => void opened.refetch()}>
            Try again
          </button>
        </p>
      )}
    </section>
  );
}

/** A More folder as the shell's page: its list, and the message open beside it (over it on a phone). */
export function FolderPage(props: { shell: Shell; folder: FolderChoice }) {
  const { shell, folder } = props;
  const place = useFolderPlace(folder);
  const words = WORDS[folder.folder];
  const { layout, fitted, changeLayout } = shell;
  const widths: Record<string, string> = { "--lcr-list-w": `${fitted.list}px` };
  return (
    <main
      className="lcr-main lcr-i2 lcr-folder-page"
      style={widths}
      data-folder={folder.folder}
      data-phone={shell.onPhone || undefined}
      data-thread={place.ref !== null || undefined}
    >
      <h1 className="lcr-visually-hidden">
        {place.part ? PART_NAMES[place.part] : words.name}, {shell.current.name}
      </h1>
      <PaneDivider
        label={`Resize ${noun(words)} list`}
        className="lcr-divider-list"
        value={fitted.list}
        min={LIST.min}
        max={LIST.max}
        fromPointer={(x) =>
          x - (document.querySelector(".lcr-folder-page")?.getBoundingClientRect().left ?? 0)
        }
        onResize={(width, done) => changeLayout(resizeList(layout, width), done)}
      />
      {/* Keyed by folder: a page or an open message of one folder never shows under another. */}
      <ListColumn key={folder.folder} shell={shell} folder={folder} words={words} place={place} />
      <ReadingColumn
        key={`${folder.folder}:reading`}
        shell={shell}
        folder={folder}
        words={words}
        place={place}
      />
    </main>
  );
}
